import * as React from 'react';
import { cn } from 'src/utils/utils';

import { Button } from './button';
import { Icon } from './icon';
import type { TooltipProps } from './tooltip';
import { Tooltip } from './tooltip';
import { Typography } from './typography';

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: boolean;
  tooltipText?: string;
  actionButtonText?: string;
  onActionButtonClick?: () => void;
  leftIcon?: React.FC<React.SVGProps<SVGSVGElement>>;
  rightIcon?: React.FC<React.SVGProps<SVGSVGElement>>;
  rightIconTooltipProps?: Omit<TooltipProps, 'trigger'>;
  helperText?: string;
  helperIcon?: React.FC<React.SVGProps<SVGSVGElement>>;
  inputClassName?: string;
  onClickRightIcon?: () => void;
  showCharCount?: boolean;
  leftText?: React.ReactNode;
  rightText?: React.ReactNode;
}

function Input({
  className,
  type,
  label,
  error,
  tooltipText,
  actionButtonText,
  onActionButtonClick,
  leftIcon,
  rightIcon,
  rightIconTooltipProps,
  helperText,
  helperIcon,
  inputClassName,
  onClickRightIcon,
  showCharCount,
  leftText,
  rightText,
  ...props
}: InputProps) {
  const [value, setValue] = React.useState<string>(
    (props.value as string) || ''
  );

  // Controlled callers own the text; the internal state only backs uncontrolled use.
  const currentValue = (props.value as string) ?? value;
  const charCountVisible = showCharCount && props.maxLength !== undefined;
  const atCharLimit =
    props.maxLength !== undefined && currentValue.length >= props.maxLength;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    props.onChange?.(e);
  };

  const getActionButtonColorClass = () => {
    if (props?.disabled) {
      return 'tw:bg-disabled tw:outline-border-disabled tw:text-text-disabled';
    }

    if (error) {
      return 'tw:bg-error-dark tw:text-text-over-brand tw:outline-error-dark';
    }

    return 'tw:bg-brand tw:text-text-over-brand tw:outline-brand';
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-s">
      {label ? (
        <Typography variant="label" colorClass="tw:text-text-secondary">
          {label}
        </Typography>
      ) : null}
      <div
        className={cn(
          `
          tw:flex
          tw:items-center
          tw:justify-between
          
          tw:bg-primary
          
          tw:px-l
          
          tw:text-lg
          tw:font-regular
          tw:leading-lg
          tw:tracking-0
          tw:placeholder-text-secondary
          
          tw:outline
          tw:outline-solid
          tw:outline-border-primary
          tw:rounded-full
          
          tw:text-text-primary
          
          tw:active:bg-primary
          tw:active:outline-border-brand
          tw:hover:outline-border-primary-hover
          
          tw:has-focus-visible:outline-border-brand
          `,
          props?.disabled &&
            'tw:pointer-events-none tw:select-none tw:cursor-not-allowed tw:bg-disabled tw:outline-border-disabled tw:text-text-disabled',
          error &&
            'tw:outline-border-error tw:active:outline-border-error tw:has-focus-visible:outline-border-error tw:hover:outline-border-error',
          actionButtonText && 'tw:p-none tw:outline-none',
          className
        )}
      >
        <div
          className={cn(
            'tw:flex tw:items-center tw:w-full',
            actionButtonText &&
              `
          tw:outline
          tw:outline-solid
          tw:outline-border-primary
          tw:-outline-offset-1

          tw:rounded-full
          tw:rounded-tr-none
          tw:rounded-br-none
          
          tw:hover:outline-border-primary-hover
          tw:active:outline-border-brand

          tw:has-focus-visible:outline-border-brand
        `,
            actionButtonText &&
              error &&
              'tw:outline-border-error tw:active:outline-border-error tw:has-focus-visible:outline-border-error tw:hover:outline-border-error',
            props?.disabled &&
              'tw:pointer-events-none tw:select-none tw:cursor-not-allowed tw:bg-disabled tw:outline-border-disabled tw:text-text-disabled'
          )}
        >
          <div className="tw:flex tw:items-center tw:justify-start tw:w-full">
            {leftText ? (
              <>
                <Typography
                  variant="b1"
                  colorClass="tw:text-text-primary"
                  className="tw:mr-2xs"
                >
                  {leftText}
                </Typography>
              </>
            ) : null}
            {leftIcon ? (
              <Icon
                icon={leftIcon}
                className={cn('tw:mr-m', actionButtonText && 'tw:ml-m')}
                sizeClass="tw:w-m tw:h-m"
              />
            ) : null}
            <input
              type={type}
              data-slot="input"
              className={cn(
                'tw:w-full tw:bg-transparent tw:leading-md tw:border-none tw:outline-none tw:py-s',
                !leftIcon && actionButtonText && 'tw:ml-m',
                inputClassName
              )}
              value={value}
              {...props}
              onChange={handleChange}
            />
            {rightText ? (
              <>
                <Typography
                  variant="b1"
                  colorClass="tw:text-text-primary"
                  className="tw:ml-2xs"
                >
                  {rightText}
                </Typography>
              </>
            ) : null}
          </div>
          {rightIcon ? (
            rightIconTooltipProps ? (
              <Tooltip
                {...rightIconTooltipProps}
                trigger={
                  <span className="tw:inline-flex">
                    <Icon
                      icon={rightIcon}
                      className={cn(
                        'tw:ml-m tw:cursor-pointer',
                        error && 'tw:text-text-error',
                        actionButtonText && 'tw:mr-m'
                      )}
                      sizeClass="tw:w-m tw:h-m"
                      colorClass={
                        error ? 'tw:text-text-error' : 'tw:text-text-primary'
                      }
                    />
                  </span>
                }
              />
            ) : (
              <Icon
                icon={rightIcon}
                onClick={onClickRightIcon}
                className={cn(
                  'tw:ml-m',
                  error && 'tw:text-text-error',
                  tooltipText && 'tw:cursor-pointer',
                  actionButtonText && 'tw:mr-m',
                  onClickRightIcon && 'tw:cursor-pointer'
                )}
                sizeClass="tw:w-m tw:h-m"
                colorClass={
                  error ? 'tw:text-text-error' : 'tw:text-text-primary'
                }
              />
            )
          ) : null}
        </div>
        {actionButtonText ? (
          <Button
            size="medium"
            hierarchy={error ? 'destructive' : 'primary'}
            onClick={onActionButtonClick}
            className={cn(
              `
              tw:rounded-none
              tw:rounded-tr-full
              tw:rounded-br-full`,
              getActionButtonColorClass()
            )}
            text={actionButtonText}
          />
        ) : null}
      </div>

      {helperText || charCountVisible ? (
        <div className="tw:flex tw:items-start tw:justify-between tw:gap-m">
          {helperText ? (
            <div
              className={cn(
                'tw:flex tw:items-center tw:justify-start tw:gap-s tw:w-max tw:text-text-secondary',
                error && 'tw:text-text-error'
              )}
            >
              {helperIcon ? (
                <Icon icon={helperIcon} sizeClass="tw:w-m tw:h-m" />
              ) : null}
              <Typography variant="h5">{helperText}</Typography>
            </div>
          ) : null}
          {charCountVisible ? (
            <Typography
              variant="h6"
              className={cn(
                'tw:ml-auto',
                atCharLimit ? 'tw:text-text-error' : 'tw:text-text-tertiary'
              )}
            >
              {currentValue.length} / {props.maxLength}
            </Typography>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export { Input };
